import { List } from 'immutable';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BoatDetail, type FullBoatDetail } from '../../types/boat/BoatDetail';
import { ReduxState } from '../../types/reduxState';

export type BoatDetailsLookup = Record<number | string, BoatDetail>;

export type UseBoats = {
  allBoats: BoatDetail[];
  /** a subset of `allBoats` that are active, i.e. published. */
  publishedBoats: BoatDetail[];
  unpublishedBoats: BoatDetail[];
  boatsIndexedById: BoatDetailsLookup;
  /**
   * Boats that are `instabook_enabled` and not using `prices-api`.
   * Because boats that are `enabled_for_instabook` are managed on `/admin/prices/`
   * and should be excluded from some react-universal instabook UI
   */
  instabookableBoats: {
    all: FullBoatDetail[];
    published: FullBoatDetail[];
    unpublished: FullBoatDetail[];
  };
};

const emptyList = List();

/**
 * Returns the user's boats from the redux store
 */
const useBoats = (): UseBoats => {
  const allBoats = useSelector<ReduxState, BoatDetail[]>(
    state => state.user.get('boats', List())?.toJS() ?? emptyList,
  ).sort((a, b) => Number(b.active) - Number(a.active));
  const publishedBoats = useMemo<BoatDetail[]>(() => (
    allBoats.filter(boat => boat.active)
  ), [allBoats]);
  const unpublishedBoats = useMemo<BoatDetail[]>(
    () => allBoats.filter(boat => !boat.active),
    [allBoats],
  );
  /**
   * The boats are indexed by both the hashed id and the numeric primary key id
   */
  const boatsIndexedById = useMemo<BoatDetailsLookup>(() => ({
    ...keyBy(allBoats, boat => boat.boat_id),
    ...keyBy(allBoats, boat => boat.id),
  }), [allBoats]);

  const instabookableBoats = useMemo(() => (
    allBoats.reduce<{
      all: FullBoatDetail[]; published: FullBoatDetail[]; unpublished: FullBoatDetail[];
    }>((acc, boat) => {
      if (boat.instabook_enabled && !boat.has_calendar_pricing_enabled) {
        acc.all.push(boat);
        if (boat.active) {
          acc.published.push(boat);
        } else {
          acc.unpublished.push(boat);
        }
      }
      return acc;
    }, { all: [], published: [], unpublished: [] })
  ), [allBoats]);

  return {
    allBoats,
    publishedBoats,
    unpublishedBoats,
    boatsIndexedById,
    instabookableBoats,
  };
};

export default useBoats;
